import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { WishListGiver } from '../models/WishListGiver.interface';
import { Message } from '../models/Message.interface';
import { Feedback } from '../models/Feedback.interface';
import { makeStateKey, TransferState } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { LoggerService } from './logger.service'; 
import { Invitation } from '../models/Invitation.interface';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({
  providedIn: 'root'
})
export class GuestService {
  private baseUrl = environment.apiUrl;
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  emailGiver = '';
  sendMailGiver = true;
  codeConfirmation: string;

  constructor(private httpClient: HttpClient, private logger: LoggerService) {}

  // op server kan ik niet auth token gebruiken en dam gaat transferstate niet werken. vandar 2 methods voor giverreserved.
  getWishListDoormij(id: string, key: string): Observable<WishListGiver> {
    const encodedId = encodeURIComponent(id);
    const encodedKey = encodeURIComponent(key);
    return this.httpClient.get<WishListGiver>(`${this.baseUrl}/Guest/WishListGiver?id=${encodedId}&key=${encodedKey}`)
  }

  getWishList(id: string, key: string): Observable<WishListGiver> {
    const encodedId = encodeURIComponent(id);
    const encodedKey = encodeURIComponent(key);
    const stateKey = makeStateKey<WishListGiver>(`wishlist-${encodedId}-${encodedKey}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get<WishListGiver>(`${this.baseUrl}/Guest/WishListGiver?id=${encodedId}&key=${encodedKey}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  getImageBase64(imageBase64ID: string): Observable<string> {
    const encodedImageBase64 = encodeURIComponent(imageBase64ID);

    return this.httpClient.get(`${this.baseUrl}/Guest/ImageBase64?imageBase64ID=${encodedImageBase64}`, { 
      headers: new HttpHeaders({
        'Accept': 'text/plain'
      }),
      responseType: 'text'
    });
  }

  // op server kan ik niet auth token gebruiken en dam gaat transferstate niet werken. vandar 2 methods voor giverreserved.
  getGiverReservedDoormij(key: string): Observable<WishListGiver[]> {
    const encodedKey = encodeURIComponent(key);
    this.logger.log('getGiverReservedDoormij', encodedKey);
    return this.httpClient.get<WishListGiver[]>(`${this.baseUrl}/Guest/GiverReserved?key=${encodedKey}`);
  }

  getGiverReserved(key: string): Observable<WishListGiver[]> {
    const encodedKey = encodeURIComponent(key);
    const stateKey = makeStateKey<WishListGiver[]>(`giver-reserved-${encodedKey}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get<WishListGiver[]>(`${this.baseUrl}/Guest/GiverReserved?key=${encodedKey}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  getInvitation(key: string): Observable<Invitation> {
    console.log('getInvitation', key);
    const encodedKey = encodeURIComponent(key);
    const stateKey = makeStateKey<Invitation>(`invitation-${encodedKey}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get<Invitation>(`${this.baseUrl}/Guest/Invitation?adminKey=${encodedKey}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  getWebshops(): Observable<unknown> {
    const stateKey = makeStateKey<unknown>('webshops');

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get(`${this.baseUrl}/Guest/Webshops`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  postMarkWish(wishListID: string, wishId: number, email: string, sendMail: boolean) { 
    const encodedWishListID = encodeURIComponent(wishListID);
    const encodedEmail = encodeURIComponent(email);
    const url = `${this.baseUrl}/Guest/MarkWish?wishList=${encodedWishListID}&id=${wishId}&email=${encodedEmail}&sendMail=${sendMail}`;
    return this.httpClient.post(url, httpOptions);
  }

  postUndoMarkWish(wishListID: string, wishId: number, key: string) {
    const encodedWishListID = encodeURIComponent(wishListID);
    const encodedKey= encodeURIComponent(key);
    return this.httpClient.post(`${this.baseUrl}/Guest/GiverWishRevert?wishList=${encodedWishListID}&id=${wishId}&key=${encodedKey}`, httpOptions);
  }

  postMessage(message: Message) {
    const body = JSON.stringify(message);
    return this.httpClient.post(`${this.baseUrl}/Guest/ContactMessage`, body, httpOptions);
  }

  postFeedback(feedback: Feedback) {
    const body = JSON.stringify(feedback);
    return this.httpClient.post(`${this.baseUrl}/Guest/FeedbackMessage`, body, httpOptions);
  }

  postUnsubscribeEmail(smid: string) {
    const encodedSmid = encodeURIComponent(smid);
    return this.httpClient.post(`${this.baseUrl}/Guest/UnsubscribeEmail?smid=${encodedSmid}`, httpOptions);
  }

  postClickMail(smid: string) {
    const encodedSmid = encodeURIComponent(smid);
    return this.httpClient.post(`${this.baseUrl}/Guest/ClickMail?smid=${encodedSmid}`, httpOptions);
  }

  postFrontEndError(error: string) {
    const encodedError = encodeURIComponent(error);
    return this.httpClient.post(`${this.baseUrl}/Guest/FrontEndError?error=${encodedError}`, httpOptions).subscribe();
  }

  getSystemMessage(id: string): Observable<unknown> {
    const encodedId = encodeURIComponent(id);
    const stateKey = makeStateKey<unknown>(`system-message-${encodedId}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get(`${this.baseUrl}/Guest/SystemMessage?id=${encodedId}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }
}